import { useCallback, useEffect, useState } from "react";
import { Loader2, LogOut, Download, Search, FileText, AlertCircle, RefreshCw, BellRing, X } from "lucide-react";
import { SEO } from "@/components/site/SEO";
import {
  adminMe, adminLogin, adminLogout, adminSubmissions, downloadAdminCsv,
  adminPolicyFileUrl, adminMissedNotifications, adminSendCatchUp, FORM_OPTIONS,
} from "@/lib/admin";

const inputCls =
  "w-full rounded-md border border-taupe/50 bg-white px-3 py-2 text-sm text-legacy focus:outline-none focus:border-gold";
const labelCls = "block text-[0.68rem] font-semibold tracking-[0.12em] uppercase text-bronze mb-1";

const EMPTY_FILTERS = { search: "", date_from: "", date_to: "", form_key: "", marketing: "", cc_list: "" };

function LoginView({ onAuthed, loginEnabled }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const { status, data } = await adminLogin(password);
    setBusy(false);
    if (status === 200) { onAuthed(); return; }
    if (status === 503) setError("Admin login is not configured yet. Add FORM_ADMIN_PASSWORD_HASH to enable it.");
    else if (status === 429) setError("Too many attempts. Please wait a few minutes and try again.");
    else setError(data.message || "Incorrect password.");
  };

  return (
    <div className="min-h-screen bg-ivory flex items-center justify-center px-4">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm rounded-card bg-white border border-taupe/40 p-8">
        <h1 className="font-serif text-2xl text-legacy">Submissions Admin</h1>
        <p className="mt-2 text-sm text-slate">Sign in to review website submissions.</p>
        {!loginEnabled && (
          <p role="alert" className="mt-4 text-[0.8rem] text-bronze flex items-start gap-2">
            <AlertCircle size={15} className="shrink-0 mt-0.5" />Admin login is not configured yet.
          </p>
        )}
        <label htmlFor="admin-pw" className={`${labelCls} mt-6`}>Password</label>
        <input id="admin-pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password" data-testid="admin-password" className={inputCls} />
        {error && <p role="alert" data-testid="admin-login-error" className="mt-3 text-[0.8rem] text-bronze">{error}</p>}
        <button type="submit" disabled={busy} data-testid="admin-login-submit"
          className="btn-base btn-primary w-full mt-6 text-[0.8rem] tracking-[0.12em] uppercase disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : null}Sign In
        </button>
      </form>
    </div>
  );
}

function NotificationsPanel() {
  const [count, setCount] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState("");
  const [resultOk, setResultOk] = useState(true);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true); setError("");
    const { status, data } = await adminMissedNotifications();
    setLoading(false);
    if (status !== 200) { setError(data.message || "Failed to load missed notifications."); return; }
    setCount(data.count || 0);
    setItems(data.submissions || []);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const runCatchUp = async () => {
    setSending(true); setError(""); setResult("");
    const { status, data } = await adminSendCatchUp();
    setSending(false); setConfirming(false);
    if (status !== 200) { setError(data.message || "Catch-up failed."); return; }
    setResultOk((data.failed || 0) === 0);
    setResult(`Processed ${data.processed}. Sent ${data.sent}, failed ${data.failed}.`);
    refresh();
  };

  return (
    <section className="bg-white border border-taupe/40 rounded-card p-4 mb-5" data-testid="admin-notifications-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BellRing size={16} className="text-bronze" />
          <h2 className="font-serif text-lg text-legacy">Internal Notifications</h2>
          <span className="text-sm text-slate" data-testid="admin-missed-count">
            {loading ? "Checking\u2026" : `${count ?? 0} missed`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={refresh} className="btn-base btn-outline-dark text-xs px-3 py-2" data-testid="admin-notif-refresh"><RefreshCw size={14} />Refresh</button>
          <button onClick={() => setConfirming(true)} disabled={!count} data-testid="admin-catchup-open"
            className="btn-base btn-primary text-xs px-4 py-2 disabled:opacity-50">Send Catch-Up Notifications</button>
        </div>
      </div>
      {error && <p role="alert" className="mt-2 text-sm text-bronze flex items-center gap-2"><AlertCircle size={14} />{error}</p>}
      {result && <p role="status" data-testid="admin-catchup-result" className={`mt-2 text-sm ${resultOk ? "text-green-700" : "text-bronze"}`}>{result}</p>}
      {count > 0 && (
        <div className="mt-3 max-h-56 overflow-y-auto border border-taupe/30 rounded-md">
          <table className="w-full text-sm" data-testid="admin-missed-table">
            <thead><tr className="text-left text-[0.66rem] uppercase tracking-wider text-bronze border-b border-taupe/30">
              <th className="px-3 py-2">Date (UTC)</th><th className="px-3 py-2">Form</th>
              <th className="px-3 py-2">Name</th><th className="px-3 py-2">Email</th><th className="px-3 py-2">Status</th>
            </tr></thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.submission_id} className="border-b border-taupe/20">
                  <td className="px-3 py-2 whitespace-nowrap text-slate">{(it.submitted_at_utc || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2">{it.form_display_name}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{it.name}</td>
                  <td className="px-3 py-2">{it.email}</td>
                  <td className="px-3 py-2">{it.notification_status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" data-testid="admin-catchup-modal">
          <div className="bg-white rounded-card border border-taupe/40 p-6 max-w-md w-full">
            <div className="flex items-start justify-between">
              <h3 className="font-serif text-lg text-legacy">Send catch-up notifications?</h3>
              <button onClick={() => setConfirming(false)} className="text-slate hover:text-bronze" aria-label="Close"><X size={18} /></button>
            </div>
            <p className="mt-2 text-sm text-slate">
              This will send Shawn an internal notification email for <strong>{count}</strong> submission{count === 1 ? "" : "s"} that were previously missed. Subscribers are not emailed. This action is safe to run once.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setConfirming(false)} className="btn-base btn-outline-dark text-xs px-4 py-2">Cancel</button>
              <button onClick={runCatchUp} disabled={sending} data-testid="admin-catchup-confirm"
                className="btn-base btn-primary text-xs px-4 py-2 disabled:opacity-60">
                {sending ? <Loader2 size={14} className="animate-spin" /> : null}Yes, send {count} now
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default function AdminSubmissions() {
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [loginEnabled, setLoginEnabled] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const check = useCallback(async () => {
    const me = await adminMe();
    setAuthed(Boolean(me.authenticated));
    setLoginEnabled(Boolean(me.login_enabled));
    setChecking(false);
  }, []);

  useEffect(() => { check(); }, [check]);

  const load = useCallback(async (f) => {
    setLoading(true); setError("");
    const { status, data } = await adminSubmissions(f);
    setLoading(false);
    if (status === 401) { setAuthed(false); return; }
    if (status !== 200) { setError(data.message || "Failed to load submissions."); return; }
    setRows(data.submissions || []);
    setCount(data.count || 0);
  }, []);

  useEffect(() => { if (authed) load(EMPTY_FILTERS); }, [authed, load]);

  const applyFilters = (e) => { e.preventDefault(); load(filters); };
  const resetFilters = () => { setFilters(EMPTY_FILTERS); load(EMPTY_FILTERS); };

  const doExport = async (kind) => {
    setError(""); setNotice("");
    try {
      await downloadAdminCsv(kind, kind === "kit.csv" ? { cc_list: filters.cc_list } : filters);
      setNotice("Download started.");
    } catch (err) {
      setError(err.message);
      if (String(err.message).includes("session")) setAuthed(false);
    }
  };

  const logout = async () => { await adminLogout(); setAuthed(false); };

  if (checking) {
    return <div className="min-h-screen bg-ivory flex items-center justify-center"><Loader2 className="animate-spin text-bronze" /></div>;
  }
  if (!authed) {
    return (<><SEO title="Admin" noindex /><LoginView onAuthed={() => setAuthed(true)} loginEnabled={loginEnabled} /></>);
  }

  return (
    <div className="min-h-screen bg-ivory" data-testid="admin-submissions-page">
      <SEO title="Submissions Admin" noindex />
      <header className="border-b border-taupe/40 bg-white">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="font-serif text-xl text-legacy">Submissions Admin</h1>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm text-slate hover:text-bronze">
            <LogOut size={16} />Sign out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <NotificationsPanel />
        {/* Filters */}
        <form onSubmit={applyFilters} className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end bg-white border border-taupe/40 rounded-card p-4">
          <div className="col-span-2">
            <label htmlFor="f-search" className={labelCls}>Search name / email</label>
            <input id="f-search" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className={inputCls} data-testid="admin-search" />
          </div>
          <div>
            <label htmlFor="f-form" className={labelCls}>Form</label>
            <select id="f-form" value={filters.form_key} onChange={(e) => setFilters({ ...filters, form_key: e.target.value })} className={inputCls} data-testid="admin-form-filter">
              {FORM_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="f-mkt" className={labelCls}>Marketing</label>
            <select id="f-mkt" value={filters.marketing} onChange={(e) => setFilters({ ...filters, marketing: e.target.value })} className={inputCls} data-testid="admin-marketing-filter">
              <option value="">All</option>
              <option value="true">Opted in</option>
              <option value="false">Not opted in</option>
            </select>
          </div>
          <div>
            <label htmlFor="f-from" className={labelCls}>From date</label>
            <input id="f-from" type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label htmlFor="f-to" className={labelCls}>To date</label>
            <input id="f-to" type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className={inputCls} />
          </div>
          <div className="col-span-2 md:col-span-2">
            <label htmlFor="f-list" className={labelCls}>Intended list</label>
            <input id="f-list" value={filters.cc_list} onChange={(e) => setFilters({ ...filters, cc_list: e.target.value })} placeholder="e.g. Weekly Newsletter Sign Ups" className={inputCls} data-testid="admin-cc-filter" />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn-base btn-primary text-xs px-4 py-2" data-testid="admin-apply"><Search size={14} />Apply</button>
            <button type="button" onClick={resetFilters} className="btn-base btn-outline-dark text-xs px-3 py-2"><RefreshCw size={14} /></button>
          </div>
        </form>

        {/* Actions */}
        <div className="flex flex-wrap items-center justify-between gap-3 mt-5">
          <p className="text-sm text-slate" data-testid="admin-count">{loading ? "Loading\u2026" : `${count} submission${count === 1 ? "" : "s"}`}</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => doExport("submissions.csv")} className="btn-base btn-outline-dark text-xs px-4 py-2" data-testid="admin-export-all"><Download size={14} />All Submissions CSV</button>
            <button onClick={() => doExport("kit.csv")} className="btn-base btn-primary text-xs px-4 py-2" data-testid="admin-export-kit"><Download size={14} />Kit CSV</button>
          </div>
        </div>
        {error && <p role="alert" data-testid="admin-error" className="mt-3 text-sm text-bronze flex items-center gap-2"><AlertCircle size={15} />{error}</p>}
        {notice && <p role="status" className="mt-3 text-sm text-green-700">{notice}</p>}

        {/* Table */}
        <div className="mt-4 overflow-x-auto bg-white border border-taupe/40 rounded-card">
          <table className="w-full text-sm" data-testid="admin-table">
            <thead>
              <tr className="text-left text-[0.68rem] uppercase tracking-wider text-bronze border-b border-taupe/40">
                <th className="px-3 py-2.5">Date (UTC)</th>
                <th className="px-3 py-2.5">Form</th>
                <th className="px-3 py-2.5">Name</th>
                <th className="px-3 py-2.5">Email</th>
                <th className="px-3 py-2.5">Phone</th>
                <th className="px-3 py-2.5">Company/Insurer</th>
                <th className="px-3 py-2.5">Marketing</th>
                <th className="px-3 py-2.5">Intended List</th>
                <th className="px-3 py-2.5">Notif.</th>
                <th className="px-3 py-2.5">Kit</th>
                <th className="px-3 py-2.5">File</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan={11} className="px-3 py-8 text-center text-slate">No submissions match these filters.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.submission_id} className="border-b border-taupe/20 align-top">
                  <td className="px-3 py-2.5 whitespace-nowrap text-slate">{(r.submitted_at_utc || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2.5">{r.form_display_name}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{`${r.first_name || ""} ${r.last_name || ""}`.trim()}</td>
                  <td className="px-3 py-2.5">{r.email}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{r.phone || "\u2014"}</td>
                  <td className="px-3 py-2.5">{r.company_or_insurance_company || "\u2014"}</td>
                  <td className="px-3 py-2.5">{r.marketing_opt_in ? <span className="text-green-700 font-medium">Yes</span> : "No"}</td>
                  <td className="px-3 py-2.5">{r.intended_constant_contact_list_name || "\u2014"}</td>
                  <td className="px-3 py-2.5">{r.notification_status || "\u2014"}</td>
                  <td className="px-3 py-2.5">{r.email_marketing_sync_status || r.constant_contact_status || "\u2014"}</td>
                  <td className="px-3 py-2.5">
                    {r.has_private_file && r.policy_review_submission_id ? (
                      <a href={adminPolicyFileUrl(r.policy_review_submission_id)} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-bronze hover:text-gold" data-testid="admin-file-link">
                        <FileText size={14} />View
                      </a>
                    ) : "\u2014"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
